import { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import classes from './Transcript.module.css'
import useBotsService from '../../../services/bot/botService'
import Discovery from './discovery/Discovery'
import DiscoveryButtons from './discovery/discoveryButtons/DiscoveryButtons'
import { useBotConfig } from '../../../context/BotConfigContext'
import CopyBotMessageButton from './CopyBotMessageButton'
import ChatBubble from './ChatBubble/ChatBubble'
import LiveThinkingStream from './LiveThinkingStream/LiveThinkingStream'
import { resolveRuntimeErrorMessage } from '../../../lib/runtimeErrorMessages';

function hasBotReplyStarted(entry) {
  return Boolean(entry?.text)
}

function hasDiscoveryChildren(button) {
  return Array.isArray(button?.children) && button.children.length > 0
}

function Transcript() {
    const botService = useBotsService()
    const config = useBotConfig()
    const { texts } = useSelector(state => state.translate)
    const session = botService.getCurrentSession()
    const transcript = botService.getCurrentTranscript()
    const welcome = config?.welcomeMessage?.trim()
    const rootButtons = Array.isArray(config?.discoveryButton) ? config.discoveryButton : []
    const displayTranscript = !session && welcome
        ? [{ sender: 'MODEL', text: welcome }]
        : transcript
    const isPending = botService.isPendingResponse()
    const showCopy = config?.enableBotMessageCopy === true
    const bottomOfTheChatRef = useRef()
    const [levelStack, setLevelStack] = useState([])

    useEffect(() => {
        setLevelStack([])
    }, [config?.discoveryButton, session])

    useEffect(() => {
        if (bottomOfTheChatRef) {
            bottomOfTheChatRef.current.scrollIntoView({
                behavior: "smooth", block: "nearest",
                inline: "nearest"
            });
        }
    }, [displayTranscript, isPending, levelStack])

    const atRoot = levelStack.length === 0
    const currentButtons = atRoot
        ? rootButtons
        : levelStack[levelStack.length - 1]

    const handleDiscoveryClick = async (button) => {
        const label = String(button?.text ?? '').trim()

        if (hasDiscoveryChildren(button)) {
            if (label) {
                await botService.appendDiscoveryChoice(label)
            }
            setLevelStack((prev) => [...prev, button.children])
            return
        }

        const message = String(button?.message ?? '').trim()
        if (!message) return
        setLevelStack([])
        await botService.sendMessage(message, { displayText: message })
    }

    const handleDiscoveryBack = () => {
        botService.removeLastDiscoveryChoice()
        setLevelStack((prev) => prev.slice(0, -1))
    }

    const renderItems = []
    displayTranscript.forEach((m, i) => {
        if (m.sender === 'USER') {
            renderItems.push({
                type: 'user',
                key: `user-${i}-${m.text}`,
                message: m,
            })
            return
        }
        if (m.sender !== 'MODEL') return
        const isLastModel = i === displayTranscript.length - 1
        const isThinkingPhase = isPending
            && isLastModel
            && m.isStreaming
            && !hasBotReplyStarted(m)
        renderItems.push({
            type: 'model-group',
            key: `model-${i}-${m.userText || m.text || i}`,
            message: m,
            isThinkingPhase,
            thinkingText: m.streamThinking || '',
        })
    })

    const bubbles = renderItems.map((item) => {
        if (item.type === 'user') {
            const m = item.message
            return (
                <div key={item.key} className={classes.UserBubbleContainer}>
                    <div className={classes.UserBubble}>
                        <ChatBubble message={m} urlClickText={config?.urlClickText} />
                    </div>
                </div>
            )
        }
        const m = item.message
        const showModelBubble = Boolean(m.text) || (!item.isThinkingPhase && m.isStreaming)
        const errorText = m.errorKey
            ? resolveRuntimeErrorMessage(m.errorKey, texts)
            : m.errorMessage
        return (
            <div key={item.key} className={classes.ModelBubbleContainer}>
                <div className={classes.ModelBubbleWrapper}>
                    {item.isThinkingPhase ?
                        <LiveThinkingStream
                            text={item.thinkingText}
                            isActive={isPending}
                        />
                        : null}
                    {showModelBubble ?
                        <div className={classes.ModelBubble}>
                            <ChatBubble
                                message={m}
                                urlClickText={config?.urlClickText}
                                isStreaming={m.isStreaming === true}
                            />
                        </div>
                        : null}
                    {!item.isThinkingPhase && errorText ?
                        <div className={classes.InlineError}>{errorText}</div>
                        : null}
                    {!item.isThinkingPhase && showCopy && m.text ?
                        <CopyBotMessageButton text={m.text} />
                        : null}
                </div>
            </div>
        )
    })

    return (
        <div className={classes.Transcript}>
            <div className={classes.Discovery}>
                <Discovery
                    showButtons={atRoot}
                    buttons={rootButtons}
                    onButtonClick={handleDiscoveryClick}
                />
            </div>
            {bubbles}
            {!atRoot ? (
                <DiscoveryButtons
                    buttons={currentButtons}
                    onButtonClick={handleDiscoveryClick}
                    onBack={handleDiscoveryBack}
                />
            ) : null}
            <div ref={bottomOfTheChatRef} />
        </div>
    );
}

export default Transcript;
